import cn from 'classnames'
import { Loader } from 'src/design-system'
import { observer } from 'mobx-react-lite'
import * as React from 'react'
import { store } from 'store/index'
import { UserWithProficiency } from '../table/data'
import { ProficiencyLevelRow } from './proficiency-level-row'

type FilterType = 'all' | 'declared' | 'inferred'

type PeopleSectionProps = {
  usersWithProficiency: UserWithProficiency[]
}

const filterOptions: { value: FilterType; label: string }[] = [
  { value: 'all', label: 'All status' },
  { value: 'declared', label: 'Declared' },
  { value: 'inferred', label: 'Inferred' },
]

export const PeopleSection: React.FC<PeopleSectionProps> = observer(
  ({ usersWithProficiency }) => {
    const [filter, setFilter] = React.useState<FilterType>('all')
    const [loading, setLoading] = React.useState(true)

    React.useEffect(() => {
      async function fetchUsers() {
        // Check if we need to fetch users - if any user from the skill is missing
        const missingUsers = usersWithProficiency.some(
          (u) => !store.users.byId(u.userId)
        )

        if (missingUsers) {
          await store.users.fetchAll({
            include: ['position'],
            page: { size: 9999 },
          })
        }
        setLoading(false)
      }

      fetchUsers()
    }, [usersWithProficiency])

    const filteredUsers = React.useMemo(() => {
      if (filter === 'all') return usersWithProficiency
      if (filter === 'declared') {
        return usersWithProficiency.filter(
          (u) => u.source === 'assigned' || u.source === 'self_declared'
        )
      }
      return usersWithProficiency.filter((u) => u.source === 'implied')
    }, [usersWithProficiency, filter])

    const usersByProficiency = React.useMemo(() => {
      const grouped: Record<number, UserWithProficiency[]> = {
        1: [],
        2: [],
        3: [],
        4: [],
        5: [],
      }

      filteredUsers.forEach((user) => {
        const level = user.proficiencyLevel || 1
        if (level >= 1 && level <= 5) {
          grouped[level].push(user)
        }
      })

      return grouped
    }, [filteredUsers])

    if (loading) {
      return (
        <div className="flex flex-col gap-4">
          <h3 className="text-[28px] font-medium text-gray-900">
            People with skill
          </h3>
          <div className="flex justify-center py-8">
            <Loader />
          </div>
        </div>
      )
    }

    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-[23px] text-gray-900 mb-0">
            People with skill
          </h3>
          <div className="flex items-center bg-gray-50/50 rounded-full p-1 h-[34px]">
            {filterOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setFilter(option.value)}
                className={cn(
                  'text-sm transition-colors border-0 cursor-pointer px-2 py-1 rounded-full h-[30px]',
                  filter === option.value
                    ? 'bg-white text-gray-900 font-medium shadow-sm'
                    : 'bg-transparent text-gray-500 hover:text-gray-700'
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col">
          {[1, 2, 3, 4, 5].map((level) => (
            <ProficiencyLevelRow
              key={level}
              level={level}
              users={usersByProficiency[level]}
            />
          ))}
        </div>
      </div>
    )
  }
)
